import { captureException } from "@sentry/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { regionKey } from "utils/regionKey";
import { fetchLidarUploadStatus } from "./api";
import type { ProjectLidarUploadStatus } from "./schemas";

type StatusesByReference = Map<string, ProjectLidarUploadStatus | null>;

export default function useLidarUploadStatus(projectReferences: string[]) {
  const [statusesByReference, setStatusesByReference] =
    useState<StatusesByReference>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [lastLoadedAt, setLastLoadedAt] = useState<Date | null>(null);
  const pendingReferences = useRef(new Set<string>());

  const load = useCallback(
    async (referencesToLoad: string[], refresh = false) => {
      for (const reference of referencesToLoad) {
        pendingReferences.current.add(reference);
      }
      setLoading(true);
      setError(false);
      try {
        const loadedStatuses = await fetchLidarUploadStatus(
          referencesToLoad,
          refresh
        );
        setStatusesByReference((current) => {
          const next = new Map(current);
          for (const reference of referencesToLoad) next.set(reference, null);
          for (const projectStatus of loadedStatuses) {
            next.set(
              regionKey(projectStatus.region, projectStatus.id),
              projectStatus
            );
          }
          return next;
        });
        setLastLoadedAt(new Date());
      } catch (loadError) {
        captureException(loadError, {
          tags: { feature: "views.LidarUploadStatus.load" },
        });
        setError(true);
      } finally {
        for (const reference of referencesToLoad) {
          pendingReferences.current.delete(reference);
        }
        setLoading(pendingReferences.current.size > 0);
      }
    },
    []
  );

  useEffect(() => {
    const unloadedReferences = projectReferences.filter(
      (reference) =>
        !statusesByReference.has(reference) &&
        !pendingReferences.current.has(reference)
    );
    if (unloadedReferences.length > 0) load(unloadedReferences);
  }, [projectReferences, statusesByReference, load]);

  const projectStatuses = useMemo(
    () =>
      projectReferences
        .map((reference) => statusesByReference.get(reference))
        .filter(
          (projectStatus): projectStatus is ProjectLidarUploadStatus =>
            projectStatus != null
        ),
    [projectReferences, statusesByReference]
  );

  return {
    projectStatuses,
    loading,
    error,
    lastLoadedAt,
    refresh: () => load(projectReferences, true),
  };
}
